import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Trophy, Medal, Award, TrendingUp, Target, Brain, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminGameLeaderboard = ({ user }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    centerFilter: 'all',
    gameType: '',
    difficulty: ''
  });

  useEffect(() => {
    loadCenters();
    loadLeaderboard();
  }, [filters]);

  const loadCenters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/centers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCenters(response.data);
    } catch (error) {
      console.error('Error loading centers:', error);
    }
  };

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.gameType) params.append('game_type', filters.gameType);
      if (filters.difficulty) params.append('difficulty', filters.difficulty);
      params.append('limit', '50'); // Get top 50
      
      const response = await axios.get(
        `${API}/api/game-scores/leaderboard?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLeaderboard(response.data);
      
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return null;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-white';
    if (rank === 3) return 'bg-gradient-to-r from-amber-500 to-amber-600 text-white';
    return 'bg-gray-100 text-gray-700';
  };

  const getCenterName = (centerId) => {
    const center = centers.find(c => c.id === centerId);
    return center?.name || 'Unknown';
  };

  // Filter leaderboard by center
  const filteredLeaderboard = filters.centerFilter === 'all' 
    ? leaderboard 
    : leaderboard.filter(entry => entry.center_id === filters.centerFilter);

  // Group by center for center-wise view
  const centerGroups = {};
  leaderboard.forEach(entry => {
    const centerId = entry.center_id || 'unknown';
    if (!centerGroups[centerId]) {
      centerGroups[centerId] = [];
    }
    centerGroups[centerId].push(entry);
  });

  // Calculate center statistics
  const centerStats = Object.keys(centerGroups).map(centerId => {
    const entries = centerGroups[centerId];
    const totalGames = entries.reduce((sum, e) => sum + e.total_games, 0);
    const avgAccuracy = entries.reduce((sum, e) => sum + e.best_accuracy, 0) / entries.length;
    const topStudent = entries[0]; // Highest ranked in that center
    
    return {
      centerId,
      centerName: getCenterName(centerId),
      studentCount: entries.length,
      totalGames,
      avgAccuracy: avgAccuracy.toFixed(1),
      topStudent
    };
  }).sort((a, b) => b.avgAccuracy - a.avgAccuracy);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
          <Trophy className="w-8 h-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Admin Game Leaderboard
          </h2>
          <p className="text-gray-600 text-sm">View performance across all centers</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Game Type</label>
            <Select value={filters.gameType} onValueChange={(value) => setFilters({ ...filters, gameType: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Games" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Games</SelectItem>
                <SelectItem value="flash_number">Flash Number</SelectItem>
                <SelectItem value="math_operations">Math Operations</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Difficulty</label>
            <Select value={filters.difficulty} onValueChange={(value) => setFilters({ ...filters, difficulty: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Difficulties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Difficulties</SelectItem>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="1_digit">1 Digit</SelectItem>
                <SelectItem value="2_digit">2 Digits</SelectItem>
                <SelectItem value="3_digit">3 Digits</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Filter by Center</label>
            <Select value={filters.centerFilter} onValueChange={(value) => setFilters({ ...filters, centerFilter: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Centers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Centers</SelectItem>
                {centers.map(center => (
                  <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="students" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="students">Student Leaderboard</TabsTrigger>
          <TabsTrigger value="centers">Center Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="space-y-4 mt-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredLeaderboard.length === 0 ? (
            <Card className="p-12 text-center">
              <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No scores yet</h3>
              <p className="text-gray-600">Students haven't played any games yet!</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredLeaderboard.map((entry, idx) => (
                <Card key={entry.user_id} className="p-4 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${getRankBadge(idx + 1)}`}>
                      {getRankIcon(idx + 1) || `#${idx + 1}`}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{entry.user_name}</h4>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {getCenterName(entry.center_id)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {entry.total_games} games played
                      </div>
                    </div>

                    <div className="text-right space-y-1">
                      <div className="text-2xl font-bold text-blue-600">
                        {entry.best_accuracy.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">
                        Best: {entry.best_score} correct
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="centers" className="space-y-4 mt-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : centerStats.length === 0 ? (
            <Card className="p-12 text-center">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No center data</h3>
              <p className="text-gray-600">No game activity across centers yet!</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {centerStats.map((stat, idx) => (
                <Card key={stat.centerId} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        idx === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white' :
                        idx === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-white' :
                        idx === 2 ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        #{idx + 1}
                      </div>
                      <div>
                        <h4 className="font-bold text-lg">{stat.centerName}</h4>
                        <p className="text-sm text-gray-600">{stat.studentCount} active students</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">{stat.avgAccuracy}%</div>
                      <div className="text-xs text-gray-500">Avg Accuracy</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{stat.totalGames}</div>
                      <div className="text-xs text-gray-600">Total Games</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{stat.studentCount}</div>
                      <div className="text-xs text-gray-600">Students</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-amber-600 truncate">
                        {stat.topStudent?.user_name || '-'}
                      </div>
                      <div className="text-xs text-gray-600">Top Student</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminGameLeaderboard;
